import React, { useState, useEffect, useRef } from 'react';
import './ModeratorAgent.css';

function fmtDayCell(minutes, punishments) {
  const h = Math.floor((minutes || 0) / 60);
  const m = Math.floor((minutes || 0) % 60);
  return {
    time: minutes > 0 ? `${h}ч ${m}м` : '0м',
    pun: punishments > 0 ? String(punishments) : '—',
  };
}

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      className="ma-copy"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setOk(true);
          setTimeout(() => setOk(false), 1200);
        });
      }}
    >
      {ok ? '✓' : 'Копировать'}
    </button>
  );
}

function StatsBlock({ data }) {
  if (!data?.tableRows?.length) return null;
  const dayHeaders = data.tableRows[0]?.days || [];
  return (
    <div className="ma-block">
      <div className="ma-block-head">
        <span>{data.title}</span>
        <CopyBtn text={data.text} />
      </div>
      {data.tops?.length > 0 && (
        <div className="ma-block-sub">
          {data.tops.map((t) => (
            <div key={t.rank}>
              {t.rank}. @{t.nickname} — {t.punishments} нак., {t.hours} ч
            </div>
          ))}
        </div>
      )}
      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead>
            <tr>
              <th>Serv</th>
              <th>Nick</th>
              <th>ID</th>
              <th>Итог</th>
              {dayHeaders.map((d) => (
                <th key={d.day} colSpan={2}>
                  {d.label} {d.dateShort}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.tableRows.map((row) => (
              <tr key={`${row.server}-${row.nickname}`}>
                <td>{row.server?.replace(/^RU/, 'S') || '—'}</td>
                <td>{row.nickname}</td>
                <td>{row.staticId || '—'}</td>
                <td>
                  {row.totalHours}
                  <span className="ma-pun">{row.totalPunishments}</span>
                </td>
                {row.days.map((d) => {
                  const c = fmtDayCell(d.minutes, d.punishments);
                  const cls = d.isVacation ? 'is-vac' : d.isEmpty ? 'is-zero' : '';
                  return (
                    <React.Fragment key={d.day}>
                      <td className={cls}>{c.time}</td>
                      <td className={cls}>{c.pun}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreBlock({ data, label }) {
  const text = data?.text || '';
  if (!text) return null;
  return (
    <div className="ma-block">
      <div className="ma-block-head">
        <span>{label || data.periodLabel || ''}</span>
        <CopyBtn text={text} />
      </div>
      <pre className="ma-pre">{text}</pre>
    </div>
  );
}

function MessageBlocks({ blocks }) {
  if (!blocks?.length) return null;
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'text') {
          return (
            <div key={i} className="ma-text">
              {b.text}
            </div>
          );
        }
        if (b.type === 'stats') return <StatsBlock key={i} data={b.data} />;
        if (b.type === 'payouts') return <PreBlock key={i} data={b.data} label="Выплаты" />;
        if (b.type === 'discipline') return <PreBlock key={i} data={b.data} label="Дисциплина" />;
        return null;
      })}
    </>
  );
}

const ModeratorAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/agent/ask', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          blocks: data.blocks,
          periodLabel: data.periodLabel,
        },
      ]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', blocks: [{ type: 'text', text: e.message || 'Ошибка' }] }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="moderator-agent">
      <div className="ma-thread">
        <div className="ma-thread-inner">
          {messages.length === 0 && !loading && (
            <div className="ma-idle" aria-hidden="true" />
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`ma-turn ma-turn--${msg.role}`}>
              {msg.role === 'user' ? (
                <div className="ma-user">{msg.text}</div>
              ) : (
                <div className="ma-assistant">
                  {msg.periodLabel && <div className="ma-period">{msg.periodLabel}</div>}
                  <MessageBlocks blocks={msg.blocks} />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="ma-turn ma-turn--assistant">
              <div className="ma-assistant">
                <div className="ma-loading" />
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      </div>

      <div className="ma-composer-wrap">
        <div className="ma-composer">
          <textarea
            ref={inputRef}
            className="ma-composer-input"
            rows={1}
            placeholder="Статистика MRP, выплаты, дисциплина, TOP 2…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={loading}
          />
          <button
            type="button"
            className="ma-composer-send"
            onClick={send}
            disabled={loading || !input.trim()}
            aria-label="Отправить"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L10 14H4L18 22L16 10H22L12 2Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModeratorAgent;
